// Small building blocks shared by the documents screens and the shared LinkedDocuments/DocumentPicker.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { FileImage, FileText, FileType2, File as FileIcon, Tag, X } from 'lucide-react';
import {
  DOCUMENT_CATEGORY_LABELS,
  MAX_DOCUMENT_TAGS,
  normaliseTags,
  OCR_STATUS_LABELS,
  type DocumentCategory,
  type DocumentFileKind,
  type EntityType,
  type OcrStatus,
} from '@seventide/types';
import { Badge, StatusPill } from '../../../components/ui';
import { controlClass, useFieldControl } from '../../../components/ui/fieldContext';
import { cn } from '../../../lib/cn';
import { wsPath } from '../../../lib/api';

export function CategoryBadge({ category, className }: { category: DocumentCategory; className?: string }) {
  return (
    <Badge tone="primary" className={className}>
      {DOCUMENT_CATEGORY_LABELS[category]}
    </Badge>
  );
}

const OCR_TONES: Record<OcrStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  none: 'neutral',
  pending: 'info',
  processing: 'info',
  done: 'success',
  failed: 'warning',
  not_applicable: 'neutral',
};

export function OcrStatusPill({ status, className }: { status: OcrStatus; className?: string }) {
  return (
    <StatusPill tone={OCR_TONES[status]} className={className}>
      {OCR_STATUS_LABELS[status]}
    </StatusPill>
  );
}

export function FileKindIcon({ kind, className }: { kind: DocumentFileKind | null; className?: string }) {
  const Icon = kind === 'pdf' ? FileText : kind === 'docx' ? FileType2 : kind ? FileImage : FileIcon;
  return <Icon aria-hidden="true" className={className} />;
}

/**
 * Thumbnail made on the device at upload (served privately by the API), or a file-type icon. Decorative: the
 * title next to it carries the meaning.
 */
export function DocumentThumb({
  wsId,
  id,
  hasThumbnail,
  kind,
  className,
}: {
  wsId: string;
  id: string;
  hasThumbnail: boolean;
  kind: DocumentFileKind | null;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (hasThumbnail && !failed) {
    return (
      <img
        src={wsPath(wsId, 'documents', id, 'renditions', 'thumb')}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className={cn('h-full w-full object-cover', className)}
      />
    );
  }
  return (
    <span className={cn('flex h-full w-full items-center justify-center bg-primary-soft text-primary', className)}>
      <FileKindIcon kind={kind} className="h-10 w-10" />
    </span>
  );
}

export function TagList({ tags, max = 4, className }: { tags: string[]; max?: number; className?: string }) {
  if (tags.length === 0) return null;
  const shown = tags.slice(0, max);
  return (
    <ul className={cn('flex flex-wrap gap-1', className)} aria-label="Tags">
      {shown.map((t) => (
        <li key={t}>
          <Badge tone="neutral" className="font-normal">
            <Tag aria-hidden="true" className="h-3.5 w-3.5" />
            {t}
          </Badge>
        </li>
      ))}
      {tags.length > max && (
        <li>
          <Badge tone="neutral" className="font-normal">
            +{tags.length - max} more
          </Badge>
        </li>
      )}
    </ul>
  );
}

/**
 * Tag entry: type a tag and press Enter (or a comma) to add it; existing tags are suggested. Use inside <Field>
 * (the text box gets the Field's label, hint and error).
 */
export function TagInput({
  value,
  onChange,
  suggestions = [],
  disabled,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const listId = `tags${useId().replace(/:/g, '')}`;
  const control = useFieldControl({});
  const add = (raw: string) => {
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean).map((s) => s.slice(0, 40));
    if (parts.length === 0) return;
    onChange(normaliseTags([...value, ...parts]).slice(0, MAX_DOCUMENT_TAGS));
    setDraft('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };
  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Tags added">
          {value.map((t) => (
            <li key={t} className="inline-flex items-center gap-1 rounded-full border border-border-strong bg-surface-muted pl-3">
              <span>{t}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== t))}
                disabled={disabled}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-surface"
                aria-label={`Remove tag ${t}`}
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          {...control}
          className={controlClass}
          value={draft}
          list={listId}
          disabled={disabled || value.length >= MAX_DOCUMENT_TAGS}
          onChange={(e) => {
            const v = e.target.value;
            if (v.includes(',')) add(v);
            else setDraft(v);
          }}
          onKeyDown={onKey}
          onBlur={() => add(draft)}
          autoComplete="off"
          enterKeyHint="done"
        />
        <datalist id={listId}>
          {suggestions.filter((s) => !value.includes(s)).slice(0, 50).map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
    </div>
  );
}

/** Where a linked record lives in the app (paths relative to /w/:wsId). */
export const ENTITY_PATHS: Record<EntityType, (id: string) => string | null> = {
  gift: (id) => `money/gifts/${id}`,
  recipient: () => 'money/gifts',
  tax_year_summary: () => 'money/tax-years',
  transaction: () => 'money/transactions',
  account: () => 'money/accounts',
  care_event: () => 'money/care',
  care_invoice: () => 'money/care',
  care_contract: () => 'money/care/contracts',
  health_entry: () => 'health/diary',
  chc_process: () => 'health/chc',
  benefit_claim: () => 'health/attendance-allowance',
  decision: (id) => `records/decisions/${id}`,
  attorney_expense: () => 'records/attorney-report',
  document: (id) => `records/documents/${id}`,
  principal: () => 'more/principal',
  legal_instrument: () => 'more/principal',
  consent_record: () => 'more/principal',
};

export function entityHref(wsId: string, type: EntityType, id: string): string | null {
  const p = ENTITY_PATHS[type]?.(id);
  return p ? `/w/${wsId}/${p}` : null;
}

/** Object URL for a Blob, revoked when it changes or on unmount (StrictMode-safe). */
export function useObjectUrl(blob: Blob | null | undefined): string | null {
  const url = useMemo(() => (blob && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null), [blob]);
  const current = useRef<string | null>(null);
  useEffect(() => {
    current.current = url;
    return () => {
      current.current = null;
      if (!url) return;
      // Revoke after this tick unless the same URL was re-attached (React StrictMode re-runs effects).
      setTimeout(() => {
        if (current.current !== url) URL.revokeObjectURL(url);
      }, 0);
    };
  }, [url]);
  return url;
}
